import dayjs, { Dayjs } from 'dayjs';

import {
  DATE_FORMAT_MEDIUM,
  DATE_FORMAT_SHORT,
  FRONTEND_BASE_URL,
} from '@constants';

/**
 * Share artwork is authored at 4:5 but not at a fixed pixel size. Crawlers use
 * these to reserve the right shape before the image downloads, so the ratio is
 * the part that has to be right.
 */
export const SHARE_IMAGE_WIDTH = 1080;
export const SHARE_IMAGE_HEIGHT = 1350;

/**
 * A joint series is sold as a single thing across every one of its days, so it
 * gets one link and one preview describing the series. Anything else is
 * described by the event itself.
 */
const isJointSeries = (eventSeries?: IEventSeries) =>
  Boolean(eventSeries?.IsJointEvent);

const parseDate = (value?: string | Date): Dayjs | undefined => {
  if (!value) {
    return undefined;
  }
  const date = dayjs(value);
  return date.isValid() ? date : undefined;
};

/** '21–23 September 2026', collapsing whatever the two ends share. */
const formatRange = (from: Dayjs, to: Dayjs) => {
  if (from.isSame(to, 'day')) {
    return from.format(DATE_FORMAT_MEDIUM);
  }
  if (from.isSame(to, 'month')) {
    return `${from.format('D')}–${to.format(DATE_FORMAT_MEDIUM)}`;
  }
  if (from.isSame(to, 'year')) {
    return `${from.format(DATE_FORMAT_SHORT)} – ${to.format(
      DATE_FORMAT_MEDIUM
    )}`;
  }
  return `${from.format(DATE_FORMAT_MEDIUM)} – ${to.format(
    DATE_FORMAT_MEDIUM
  )}`;
};

const formatDateRange = (start?: string | Date, end?: string | Date) => {
  const from = parseDate(start);
  const to = parseDate(end);
  if (!from || !to) {
    return (from ?? to)?.format(DATE_FORMAT_MEDIUM);
  }
  return formatRange(from, to);
};

/**
 * A joint series does not always carry its own dates - staging series 22 has
 * neither - so fall back to the span its events actually cover rather than
 * publishing a preview with no date on it at all.
 */
const formatEventSpan = (events?: IEvent[]) => {
  const dates = (events ?? [])
    .map(candidate => parseDate(candidate.Date))
    .filter((date): date is Dayjs => Boolean(date))
    .sort((a, b) => a.valueOf() - b.valueOf());
  return dates.length > 0
    ? formatRange(dates[0], dates[dates.length - 1])
    : undefined;
};

export const resolveShareTitle = (event: IEvent, eventSeries?: IEventSeries) =>
  isJointSeries(eventSeries)
    ? eventSeries?.Name ?? ''
    : event.Name || eventSeries?.Name || '';

export const resolveShareImage = (event: IEvent, eventSeries?: IEventSeries) =>
  (isJointSeries(eventSeries)
    ? eventSeries?.Background || event.Background
    : event.Background || eventSeries?.Background) || undefined;

export const resolveShareDate = (
  event: IEvent,
  eventSeries?: IEventSeries,
  events?: IEvent[]
) => {
  const ownDate = parseDate(event.Date)?.format(DATE_FORMAT_MEDIUM);
  if (!isJointSeries(eventSeries)) {
    return ownDate;
  }
  return (
    formatDateRange(eventSeries?.StartDate, eventSeries?.EndDate) ??
    formatEventSpan(events) ??
    ownDate
  );
};

/**
 * 'Dublin 2 • 21 September 2026', dropping either half when it is missing
 * rather than leaving a stranded separator behind.
 */
export const buildShareDescription = ({
  event,
  events,
  eventSeries,
  venue,
}: {
  event: IEvent;
  events?: IEvent[];
  eventSeries?: IEventSeries;
  venue?: IVenue;
}) => {
  const city = venue?.City?.trim();
  const date = resolveShareDate(event, eventSeries, events);
  return [city, date].filter(Boolean).join(' • ') || undefined;
};

/**
 * A joint series shares one link, and it points at the last event to end so a
 * live series never resolves to a day that is already over.
 */
export const resolveCanonicalEventId = (
  event: IEvent,
  events?: IEvent[],
  eventSeries?: IEventSeries
) => {
  if (!isJointSeries(eventSeries) || !events?.length) {
    return event.ID;
  }
  const latest = events.reduce<IEvent | undefined>((latest, candidate) => {
    const ends = Date.parse(candidate.Ends);
    if (Number.isNaN(ends)) {
      return latest;
    }
    return !latest || ends > Date.parse(latest.Ends) ? candidate : latest;
  }, undefined);
  return latest?.ID ?? event.ID;
};

export const buildEventUrl = (eventId: number) =>
  `${FRONTEND_BASE_URL}/event/${eventId}`;

/**
 * A hidden event is unlisted: it must not be indexed, and crawlers must not
 * follow out of it either. Listing such pages anywhere central - robots.txt,
 * llms.txt - would itself disclose them, so the control belongs on the page.
 */
export const isEventHidden = (event: IEvent) => Boolean(event.IsHidden);

/** Only these pages carry structured data and may be indexed. */
export const isEventIndexable = (event: IEvent, events?: IEvent[]) =>
  !isEventHidden(event) && !eventHasEnded(event, events);

/** Mirrors the 'This Event Has Ended' test on the landing page. */
export const eventHasEnded = (event: IEvent, events?: IEvent[]) => {
  const now = Date.now();
  const isUpcoming = (candidate: IEvent) =>
    Boolean(candidate.Ends) && now < Date.parse(candidate.Ends);
  return !isUpcoming(event) && !events?.some(isUpcoming);
};
